import {useState} from 'react'
import {TriangleDownIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, Button} from '@primer/react'
import {useCurrentRepository} from '@github-ui/current-repository'
import {verifiedFetch} from '@github-ui/verified-fetch'
import {useClickAnalytics} from '@github-ui/use-analytics'
import type {TemplateButtonProps} from './types'

const GENERIC_ERROR = 'Could not create a codespace. Try again.'

function useTemplateCodespaceCreate(onError?: (error: Error) => void) {
  const repo = useCurrentRepository()
  const [isCreating, setIsCreating] = useState(false)

  const handleCreate = async () => {
    setIsCreating(true)

    try {
      const formData = new FormData()
      formData.set('codespace[template_repository_id]', String(repo.id))
      formData.set('codespace[repository_id]', String(repo.id))
      formData.set('codespace[ref]', repo.defaultBranch)

      const response = await verifiedFetch('/codespaces', {
        method: 'POST',
        body: formData,
        headers: {Accept: 'application/json'},
      })

      if (!response.ok) {
        let json = null
        try {
          json = await response.json()
        } catch {
          // ignore parse errors
        }
        onError?.(new Error(json?.error ?? GENERIC_ERROR))
        return
      }

      const json = await response.json()
      const url = json.codespace_url ?? json.loading_url

      if (url) {
        window.open(url, '_blank')
      } else {
        onError?.(new Error(GENERIC_ERROR))
      }
    } catch {
      onError?.(new Error(GENERIC_ERROR))
    } finally {
      setIsCreating(false)
    }
  }

  return {isCreating, handleCreate}
}

/**
 * "Use this template" button for template repository headers.
 *
 * Two modes:
 * - Without codespaces: simple primary button linking to /generate
 * - With codespaces: dropdown with "Create a new repository" and "Open in a codespace"
 */
export function TemplateButton({templatePath, showTemplateButton, hasCodespacesAccess, onError}: TemplateButtonProps) {
  const {sendClickAnalyticsEvent} = useClickAnalytics()

  if (!showTemplateButton) {
    return null
  }

  if (!hasCodespacesAccess) {
    return (
      <Button
        as="a"
        href={templatePath}
        variant="primary"
        size="small"
        data-testid="use-template-button"
        onClick={() =>
          sendClickAnalyticsEvent({
            category: 'REPOSITORY_OVERVIEW',
            action: 'click:use_template',
            label: 'location:header;ref_cta:use_template_button',
          })
        }
      >
        Use this template
      </Button>
    )
  }

  return (
    <TemplateDropdown templatePath={templatePath} onError={onError} sendClickAnalyticsEvent={sendClickAnalyticsEvent} />
  )
}

function TemplateDropdown({
  templatePath,
  onError,
  sendClickAnalyticsEvent,
}: {
  templatePath: string
  onError?: (error: Error) => void
  sendClickAnalyticsEvent: ReturnType<typeof useClickAnalytics>['sendClickAnalyticsEvent']
}) {
  const {isCreating, handleCreate} = useTemplateCodespaceCreate(onError)

  function handleOpenCodespace() {
    sendClickAnalyticsEvent({
      category: 'REPOSITORY_OVERVIEW',
      action: 'click:use_template_codespace',
      label: 'location:header;ref_cta:use_template_dropdown',
    })
    handleCreate()
  }

  return (
    <ActionMenu>
      <ActionMenu.Anchor>
        <Button
          variant="primary"
          size="small"
          loading={isCreating}
          trailingAction={TriangleDownIcon}
          disabled={isCreating}
          data-testid="use-template-button"
        >
          Use this template
        </Button>
      </ActionMenu.Anchor>
      <ActionMenu.Overlay width="auto">
        <ActionList>
          <ActionList.LinkItem
            href={templatePath}
            data-testid="template-create-repo"
            onSelect={() =>
              sendClickAnalyticsEvent({
                category: 'REPOSITORY_OVERVIEW',
                action: 'click:use_template',
                label: 'location:header;ref_cta:use_template_dropdown',
              })
            }
          >
            Create a new repository
          </ActionList.LinkItem>
          <ActionList.Item onSelect={handleOpenCodespace} data-testid="template-open-codespace">
            Open in a codespace
          </ActionList.Item>
        </ActionList>
      </ActionMenu.Overlay>
    </ActionMenu>
  )
}
